import { Component, lazy, Suspense, type ReactNode } from 'react';
import { Banner, Button, Dialog, Skeleton } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { usePortalOverview } from '../portal-api';
import styles from './portal.module.css';

// The portal's overview is loaded with the preview only (the portal module is a separate part of the SPA).
const OverviewContent = lazy(() =>
  import('../../portal/overview/OverviewContent').then((module) => ({ default: module.OverviewContent })),
);

/** A failed chunk load of the preview stays inside the dialog instead of replacing the record. */
class PreviewBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override render(): ReactNode {
    if (this.state.failed) {
      return (
        <Banner tone="danger">
          Die Portalansicht konnte nicht geladen werden. Bitte laden Sie die Seite neu.
        </Banner>
      );
    }
    return this.props.children;
  }
}

function PreviewSkeleton() {
  return (
    <div className={styles.skeletonRows} aria-busy="true">
      <Skeleton variant="block" height={120} />
      <Skeleton variant="block" height={160} />
    </div>
  );
}

export interface PortalPreviewDialogProps {
  open: boolean;
  contactId: string;
  contactName: string;
  onClose: () => void;
}

/**
 * „Portalansicht öffnen" (Mandantenportal 27): the customer's overview (Portal 14–17 / 15) from
 * `GET /contacts/:id/portal/overview`, drawn by the portal's own `OverviewContent` in read-only preview mode.
 */
export function PortalPreviewDialog({ open, contactId, contactName, onClose }: PortalPreviewDialogProps) {
  const overview = usePortalOverview(contactId, open);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={960}
      eyebrow="Portalansicht"
      title={`So sieht ${contactName} das Portal`}
      initialFocus="container"
      footerNote="Nur Ansicht — Aktionen sind in der Vorschau deaktiviert."
      footer={
        <Button variant="secondary" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      {overview.isPending ? <PreviewSkeleton /> : null}
      {overview.isError ? (
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void overview.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(overview.error)}
        </Banner>
      ) : null}
      {overview.data ? (
        <div className={styles.preview}>
          <PreviewBoundary>
            <Suspense fallback={<PreviewSkeleton />}>
              <OverviewContent data={overview.data} preview />
            </Suspense>
          </PreviewBoundary>
        </div>
      ) : null}
    </Dialog>
  );
}
